import CustomDatePicker from 'Components/CustomDatePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import MultiSelect, { type OptionType } from 'Components/MultiSelect/MultiSelect'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import ReportDownload from 'Services/SalesManagement/Features/Report/Components/ReportDownload'
import ReportProgress from 'Services/SalesManagement/Features/Report/Components/ReportProgress'
import {
  createTicketSaleReport,
  fetchTicketSaleReport,
  ReportGenerationStatus,
  resetTicketSaleReportWebsocket,
  TICKET_SALE_REPORT_PREFIX,
  type TicketSaleReportParamShape
} from 'Services/SalesManagement/Features/Report/reportSlice'
import { useGetAllTicketSaleTargetsQuery } from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import useTypedSelector, { useAppDispatch } from 'Store'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import GenerateReportIcon from '@mui/icons-material/CloudUpload'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { type FormEvent, useState } from 'react'
import { t } from 'ttag'

export default function TicketSaleReport() {
  const dispatch = useAppDispatch()

  const {
    ticketSaleReportLoading,
    singleTicketSaleReport,
    ticketSaleReportGenerationStatus,
    ticketSaleReportGenerationTask
  } = useTypedSelector(state => state.report)

  const { authorities } = useAuthorityContext()
  const { data: ticketSaleTargets, isFetching: isTicketSaleTargetsFetching } =
    useGetAllTicketSaleTargetsQuery({
      disable_pagination: true
    })

  const [params, setParams] = useState<TicketSaleReportParamShape>({
    authorities: [],
    targets: [],
    from_date: '',
    to_date: ''
  })

  const handleReportGenerate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Reset the report generation state before starting a new one
    dispatch(resetTicketSaleReportWebsocket())
    const reportParams: TicketSaleReportParamShape = { ...params }
    if (!reportParams.from_date) delete reportParams.from_date
    if (!reportParams.to_date) delete reportParams.to_date
    dispatch(createTicketSaleReport(reportParams))
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Report generation successfully started.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error starting the report generation.`)
      })
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }

    setParams(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const handleMultiSelectChange = (selected: string[], name: keyof TicketSaleReportParamShape) => {
    setParams(prevState => ({ ...prevState, [name]: selected }))
  }

  const authorityOptions: OptionType[] =
    authorities?.map(authority => [authority.code, authority.name]) ?? []

  const ticketSaleTargetOptions: OptionType[] =
    ticketSaleTargets?.map(target => [target.id, target.identifier]) ?? []

  const isLoading = ticketSaleReportLoading === 'pending'
  const isReportGenerationDisabled =
    ticketSaleReportGenerationStatus?.status === ReportGenerationStatus.PENDING ||
    ticketSaleReportGenerationStatus?.status === ReportGenerationStatus.PROGRESS
  const isFiltersLoading = isTicketSaleTargetsFetching

  return (
    <WithLoader loading={isFiltersLoading}>
      <Paper sx={{ width: '100%', my: 2, p: 2, pt: 1 }}>
        <StyledForm onSubmit={handleReportGenerate} isDisabled={isLoading}>
          <Box sx={{ minWidth: 200, maxWidth: 400 }}>
            <MultiSelect
              handleChange={selected => handleMultiSelectChange(selected, 'authorities')}
              label={t`Authority`}
              required={true}
              options={authorityOptions}
            />
          </Box>
          <Box sx={{ minWidth: 200, maxWidth: 400 }}>
            <MultiSelect
              handleChange={selected => handleMultiSelectChange(selected, 'targets')}
              label={t`Target`}
              options={ticketSaleTargetOptions}
            />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="from_date"
                value={params.from_date ? new Date(params.from_date) : null}
                dateToCompare={params.to_date ? new Date(params.to_date) : null}
                dateToCompareFieldName="to_date"
                isStartDate={true}
                label={t`Start date`}
              />
              <Box>{` - `}</Box>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="to_date"
                value={params.to_date ? new Date(params.to_date) : null}
                dateToCompare={params.from_date ? new Date(params.from_date) : null}
                dateToCompareFieldName="from_date"
                isStartDate={false}
                label={t`End date`}
              />
            </Box>
          </Box>
          <Box>
            <CustomLoadingButton
              isLoading={isLoading}
              type="submit"
              disabled={isReportGenerationDisabled}
              icon={<GenerateReportIcon />}
              iconPosition="start"
            >{t`Generate report`}</CustomLoadingButton>
          </Box>
          <Box sx={{ my: 4 }}>
            <ReportProgress
              reportGenerationTask={ticketSaleReportGenerationTask}
              reportPrefix={TICKET_SALE_REPORT_PREFIX}
              reportStatus={ticketSaleReportGenerationStatus}
              reportPath="reports/group-tickets/sales"
              resetReportWebsocketState={() => {
                dispatch(resetTicketSaleReportWebsocket())
              }}
            />
          </Box>
        </StyledForm>
        <ReportDownload
          singleReport={singleTicketSaleReport}
          reportGenerationStatus={ticketSaleReportGenerationStatus}
          fetchReport={reportName =>
            dispatch(fetchTicketSaleReport({ report_name: reportName, ...params })).unwrap()
          }
          isLoading={isLoading}
        />
      </Paper>
    </WithLoader>
  )
}
